import React, { useEffect, useState } from "react";

import PostRegistrationPromo from "@/common/Outro/RegistrationForm/promo/PostRegistrationPromo";
import { RegistrationFormProps } from "@/common/Outro/RegistrationForm/RegistrationForm";
import Pagination from "@/components/bild/Pagination";
import { SALUTATION_NONE_VALUE } from "@/components/Form/FormFields";
import { Participant, RegistrationStatus } from "@/service/Model";

import DataOptIn from "./components/DataOptIn/DataOptIn";
import PersonalDetails from "./components/PersonalDetails/PersonalDetails";
import * as Styled from "./styles";

// Pre-select "Keine Angabe" (ESD-2418) when nothing else provides a gender; keep a real one.
const withDefaultSalutation = (
  data: Partial<Participant>
): Partial<Participant> =>
  data.gender ? data : { ...data, gender: SALUTATION_NONE_VALUE };

const LightRegistrationForm: React.FC<RegistrationFormProps> = (
  props: RegistrationFormProps
): React.JSX.Element => {
  const { onSubmit, registrationStatus, outro, hansId, hansIdExists } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState<Partial<Participant>>(() =>
    withDefaultSalutation({ ...props.initialParticipant })
  );

  useEffect(() => {
    setFormData((prevData) => {
      const nextData = withDefaultSalutation({
        ...props.initialParticipant,
        ...prevData,
      });
      // A real stored gender arriving after mount (via initialParticipant) must replace the
      // default "Keine Angabe" (prevData already holds "none"); a genuine user choice is
      // preserved, since only the default is overridden. Mirrors the classic form.
      const incomingGender = props.initialParticipant?.gender;
      if (
        incomingGender &&
        incomingGender !== SALUTATION_NONE_VALUE &&
        prevData.gender === SALUTATION_NONE_VALUE
      ) {
        nextData.gender = incomingGender;
      }
      return nextData;
    });
  }, [props.initialParticipant]);

  const handleFormDataChange = (newData: Partial<Participant>) => {
    setFormData((prevData) => ({ ...prevData, ...newData }));
  };

  const handleSubmit = () => {
    if (onSubmit) {
      // "Keine Angabe" is a UI-only sentinel; normalize it to "" on this light submit path so the
      // backend never receives it (matches the classic convertFormData normalization).
      const participant = {
        ...formData,
        gender:
          formData.gender === SALUTATION_NONE_VALUE
            ? ""
            : (formData.gender ?? ""),
        interactionType: "",
        interactionId: "",
      };
      onSubmit(participant);
    }
  };

  const onNext = () => {
    if (currentPage < pages.length) setCurrentPage(currentPage + 1);
  };

  const onBack = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const pages = [
    <PersonalDetails
      outro={outro}
      key="personalDetails"
      formData={formData}
      onChange={handleFormDataChange}
      onNext={onNext}
    />,
    <DataOptIn
      outro={outro}
      key="dataOptIn"
      formData={formData}
      onChange={handleFormDataChange}
      onBack={onBack}
      onSubmit={handleSubmit}
      hansId={hansId}
      hansIdExists={hansIdExists}
    />,
  ];

  const renderForm = (): React.JSX.Element => {
    return (
      <>
        <Styled.Header>{`Teilnehmen (${currentPage}/2)`}</Styled.Header>
        <Styled.PaginationWrapper>
          <Pagination currentPage={currentPage} />
        </Styled.PaginationWrapper>
        {pages[currentPage - 1]}
      </>
    );
  };

  const renderFormStatus = (): React.JSX.Element => {
    return (
      <>
        <Styled.Wrapper>
          {/* ESD-2418 §3.10: drop the "Teilnehmerregistrierung" header on success — the thank-you
              stands alone (mirrors the classic form). */}
          {registrationStatus !== RegistrationStatus.Success && (
            <Styled.Header>Teilnehmerregistrierung</Styled.Header>
          )}
          <Styled.Checkmark />
          <Styled.Subheader>Vielen Dank für Ihre Teilnahme!</Styled.Subheader>
          {/* Inside the padded wrapper so the card sits right under the thank-you (no double gap). */}
          {registrationStatus === RegistrationStatus.Success &&
            outro.promo?.enabled && <PostRegistrationPromo outro={outro} />}
        </Styled.Wrapper>
      </>
    );
  };

  return (
    <>
      {!registrationStatus || registrationStatus === RegistrationStatus.None
        ? renderForm()
        : renderFormStatus()}
    </>
  );
};

export default LightRegistrationForm;
